// Giao diện thông báo donate trên OBS: kiểu khung, vị trí, cỡ chữ, hiệu ứng, nền thẻ.
// Hàm thuần dùng chung cho overlay (page-donate-livestream) và khung xem trước trong Cài đặt.

export type AlertLayout = 'classic' | 'card' | 'banner' | 'minimal' | 'glow' | 'poster';
export type AlertPosition =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'middle-left'
  | 'middle-center'
  | 'middle-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right';
export type AlertTextSize = 'sm' | 'md' | 'lg';
export type AlertAnimation = 'fade' | 'slide' | 'zoom' | 'bounce';

export interface AlertStyle {
  layout: AlertLayout;
  position: AlertPosition;
  textSize: AlertTextSize;
  animation: AlertAnimation;
  cardColor: string;
  cardOpacity: number;
  isDefault?: boolean;
}

export const ALERT_LAYOUTS: AlertLayout[] = ['classic', 'card', 'banner', 'minimal', 'glow', 'poster'];
export const ALERT_POSITIONS: AlertPosition[] = [
  'top-left',
  'top-center',
  'top-right',
  'middle-left',
  'middle-center',
  'middle-right',
  'bottom-left',
  'bottom-center',
  'bottom-right',
];
export const ALERT_TEXT_SIZES: AlertTextSize[] = ['sm', 'md', 'lg'];
export const ALERT_ANIMATIONS: AlertAnimation[] = ['fade', 'slide', 'zoom', 'bounce'];

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

// Mặc định = giao diện overlay trước khi có tính năng này (streamer chưa cài thấy y như cũ).
export const DEFAULT_ALERT_STYLE: AlertStyle = {
  layout: 'classic',
  position: 'middle-center',
  textSize: 'md',
  animation: 'fade',
  cardColor: '#000000',
  cardOpacity: 60,
};

const pick = <T extends string>(value: unknown, allowed: readonly T[], fallback: T): T =>
  typeof value === 'string' && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;

// null/undefined/'' không được coi là 0 (Number(null) === 0), mà là "không có" ⇒ mặc định.
const toOpacity = (value: unknown, fallback: number): number => {
  if (typeof value !== 'number' && !(typeof value === 'string' && value.trim() !== '')) return fallback;
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(100, Math.max(0, Math.round(n)));
};

/** Từng trường lạ (sai kiểu, ngoài tập cho phép) ⇒ về mặc định của trường đó; độ mờ kẹp 0–100. */
export const normalizeAlertStyle = (raw: any): AlertStyle => {
  const r = raw && typeof raw === 'object' ? raw : {};
  const d = DEFAULT_ALERT_STYLE;
  const style: AlertStyle = {
    layout: pick(r.layout, ALERT_LAYOUTS, d.layout),
    position: pick(r.position, ALERT_POSITIONS, d.position),
    textSize: pick(r.textSize, ALERT_TEXT_SIZES, d.textSize),
    animation: pick(r.animation, ALERT_ANIMATIONS, d.animation),
    cardColor: typeof r.cardColor === 'string' && HEX_RE.test(r.cardColor) ? r.cardColor : d.cardColor,
    cardOpacity: toOpacity(r.cardOpacity, d.cardOpacity),
  };
  if (typeof r.isDefault === 'boolean') style.isDefault = r.isDefault;
  return style;
};

const vertical = (p: AlertPosition) => p.split('-')[0] as 'top' | 'middle' | 'bottom';
const horizontal = (p: AlertPosition) => p.split('-')[1] as 'left' | 'center' | 'right';

/**
 * Lớp Tailwind căn khối thông báo trong container toàn khung.
 * Quy ước: container là `flex flex-col`, nên `justify-*` là trục DỌC (trên/giữa/dưới)
 * còn `items-*` là trục NGANG (trái/giữa/phải).
 * middle-center ⇒ 'justify-center items-center' (đúng như overlay cũ).
 */
export const positionClasses = (p: AlertPosition): string => {
  const justify = { top: 'justify-start', middle: 'justify-center', bottom: 'justify-end' }[vertical(p)];
  const items = { left: 'items-start', center: 'items-center', right: 'items-end' }[horizontal(p)];
  return `${justify} ${items}`;
};

/** Căn chữ theo cột ngang của vị trí (giữa ⇒ text-center như overlay cũ). */
export const textAlignClass = (p: AlertPosition): string =>
  ({ left: 'text-left', center: 'text-center', right: 'text-right' }[horizontal(p)]);

/** Căn các phần tử bên trong khối thông báo theo cột ngang của vị trí. */
export const innerItemsClass = (p: AlertPosition): string =>
  ({ left: 'items-start', center: 'items-center', right: 'items-end' }[horizontal(p)]);

/** Hiệu ứng "Trượt vào" đi vào từ cạnh gần nhất với vị trí. Chính giữa ⇒ từ dưới lên. */
export const slideFrom = (p: AlertPosition): 'left' | 'right' | 'top' | 'bottom' => {
  const h = horizontal(p);
  if (h === 'left') return 'left';
  if (h === 'right') return 'right';
  return vertical(p) === 'top' ? 'top' : 'bottom';
};

/** '#000000', 60 ⇒ 'rgba(0, 0, 0, 0.6)'. Hex lỗi ⇒ màu mặc định; độ mờ lỗi ⇒ độ mờ mặc định. */
export const cardBackground = (hex: string, opacity: number): string => {
  const color = typeof hex === 'string' && HEX_RE.test(hex) ? hex : DEFAULT_ALERT_STYLE.cardColor;
  const r = parseInt(color.slice(1, 3), 16);
  const g = parseInt(color.slice(3, 5), 16);
  const b = parseInt(color.slice(5, 7), 16);
  const a = toOpacity(opacity, DEFAULT_ALERT_STYLE.cardOpacity) / 100;
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

/** Cỡ chữ dòng chính / lời nhắn. md ⇒ text-4xl / text-2xl (bằng overlay cũ). */
export const textSizeClasses = (s: AlertTextSize): { main: string; message: string } => {
  switch (s) {
    case 'sm':
      return { main: 'text-2xl', message: 'text-lg' };
    case 'lg':
      return { main: 'text-6xl', message: 'text-3xl' };
    default:
      return { main: 'text-4xl', message: 'text-2xl' };
  }
};
